import { useCallback, useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import {
  Power,
  ListChecks,
  CheckCircle2,
  User as UserIcon,
  Plane,
  Briefcase,
  Users as UsersIcon,
  ChevronRight,
  MapPin,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "manifest", label: "Manifest", icon: ListChecks },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
  { key: "profile", label: "Profile", icon: UserIcon },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STATUS_LABELS = {
  confirmed: "Confirmed",
  dispatched: "Dispatched",
  en_route: "En Route",
  chauffeur_arrived: "Arrived",
  passenger_loaded: "In Transit",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function DriverDashboard() {
  const { user, profile, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("manifest");
  const [duty, setDuty] = useState(null);
  const [dutyLoading, setDutyLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAll = useCallback(async () => {
    setRunsLoading(true);
    setError("");
    try {
      const [{ data: me }, { data: active }, { data: done }] = await Promise.all([
        api.get("/drivers/me"),
        api.get("/drivers/me/bookings"),
        api.get("/drivers/me/bookings/completed"),
      ]);
      setDuty(me.duty);
      setRuns(active || []);
      setCompleted(done || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "driver") fetchAll();
  }, [user, fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">
        Loading…
      </div>
    );
  }

  if (!user) return <Navigate to="/driver/login" replace />;
  if (user.role !== "driver") {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-red-400 mb-4">
            Access denied
          </div>
          <p className="text-sm text-[#8E9AA6] font-light mb-8">
            This area is reserved for Phantasm chauffeurs.
          </p>
          <Link
            to="/"
            data-testid="driver-not-allowed-home"
            className="inline-flex items-center gap-2 bg-white text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em]"
          >
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const toggleDuty = async () => {
    if (!duty) return;
    setDutyLoading(true);
    try {
      const { data } = await api.patch("/drivers/me/duty", { on_duty: !duty.on_duty });
      setDuty(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setDutyLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/driver/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white flex flex-col pb-24">
      {/* Top brand bar */}
      <header className="sticky top-0 z-30 bg-[#0B0B0C]/90 backdrop-blur-xl border-b border-white/10 px-5 py-4 flex items-center justify-between">
        <div>
          <div className="font-serif text-xl font-semibold tracking-tight">Phantasm</div>
          <div className="text-[9px] uppercase tracking-[0.35em] text-[#8E9AA6]">
            Chauffeur
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
            On Shift
          </div>
          <div className="text-sm font-light">
            {profile?.first_name} {profile?.last_name?.[0]}.
          </div>
        </div>
      </header>

      <main className="flex-1 px-5 pt-6">
        {tab === "manifest" && (
          <>
            <DutyCard duty={duty} loading={dutyLoading} onToggle={toggleDuty} />
            <h2 className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mt-10 mb-5">
              Today's Manifest
            </h2>
            {error && <div className="text-red-400 text-sm mb-4">{error}</div>}
            {runsLoading ? (
              <div className="text-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em] py-12">
                Loading runs…
              </div>
            ) : runs.length === 0 ? (
              <div
                data-testid="driver-manifest-empty"
                className="text-center py-20 border border-dashed border-white/10"
              >
                <p className="text-sm text-[#8E9AA6] font-light">
                  No active runs. Dispatch will assign new bookings when available.
                </p>
              </div>
            ) : (
              <div data-testid="driver-manifest-list" className="space-y-4">
                {runs.map((r) => (
                  <RunCard key={r.id} run={r} onOpen={() => navigate(`/driver/runs/${r.id}`)} />
                ))}
              </div>
            )}
          </>
        )}

        {tab === "completed" && (
          <>
            <h2 className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-5">
              Recent Completed
            </h2>
            {completed.length === 0 ? (
              <div
                data-testid="driver-completed-empty"
                className="text-center py-20 border border-dashed border-white/10"
              >
                <p className="text-sm text-[#8E9AA6] font-light">
                  Your recent completed runs will appear here.
                </p>
              </div>
            ) : (
              <div data-testid="driver-completed-list" className="space-y-3">
                {completed.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`driver-completed-row-${r.id}`}
                    className="bg-[#121214]/60 border border-white/10 px-5 py-4"
                  >
                    <div className="flex justify-between items-baseline">
                      <div className="text-sm font-light">{fmtTime(r.pickup_datetime)}</div>
                      <div className="font-serif text-lg">${r.total_price.toFixed(2)}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mt-1">
                      {STATUS_LABELS[r.status]} · {r.vehicle_category.replace(/_/g, " ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "profile" && (
          <DriverProfilePanel
            user={user}
            profile={profile}
            duty={duty}
            onLogout={handleLogout}
          />
        )}
      </main>

      {/* Bottom Nav */}
      <nav
        data-testid="driver-bottom-nav"
        className="fixed bottom-0 inset-x-0 z-40 bg-[#0B0B0C]/95 backdrop-blur-xl border-t border-white/10 px-2 py-2 flex"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`driver-tab-${t.key}`}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors ${
                isActive ? "text-white" : "text-[#8E9AA6] hover:text-white"
              }`}
            >
              <Icon size={20} strokeWidth={1.5} />
              <span className="text-[9px] uppercase tracking-[0.3em]">{t.label}</span>
              {isActive && <span className="block h-px w-6 bg-white" />}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function DutyCard({ duty, loading, onToggle }) {
  const onShift = duty?.on_duty;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={loading || !duty}
      data-testid="driver-duty-toggle"
      className={`relative w-full overflow-hidden p-6 transition-all duration-500 border ${
        onShift
          ? "bg-gradient-to-br from-emerald-900/30 to-[#121214] border-emerald-400/40"
          : "bg-[#121214]/60 border-white/10"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-3">
            Duty Status
          </div>
          <div
            data-testid="driver-duty-label"
            className={`font-serif text-3xl font-semibold tracking-tight ${
              onShift ? "text-emerald-300" : "text-white"
            }`}
          >
            {onShift ? "On-Duty" : "Off-Duty"}
          </div>
          <div className="mt-2 text-xs text-[#8E9AA6] font-light">
            {onShift
              ? "Dispatch can route trips to you."
              : "Tap to go on-duty and accept runs."}
          </div>
        </div>
        <div
          className={`h-16 w-16 rounded-full flex items-center justify-center border-2 transition-colors ${
            onShift
              ? "border-emerald-400 bg-emerald-500/20 text-emerald-300"
              : "border-white/20 bg-white/5 text-[#8E9AA6]"
          }`}
        >
          <Power size={26} strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
        {onShift ? "Tap to go off-duty →" : "Tap to go on-duty →"}
      </div>
    </button>
  );
}

function RunCard({ run, onOpen }) {
  const isAirport = run.service_type === "airport";
  const isHourly = run.service_type === "hourly";
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`driver-run-card-${run.id}`}
      className="group w-full text-left bg-[#121214]/70 border border-white/10 p-5 transition-all duration-300 hover:border-white/30 active:scale-[0.99]"
    >
      <div className="flex justify-between items-baseline mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
            Pickup
          </div>
          <div className="font-serif text-xl text-white">{fmtTime(run.pickup_datetime)}</div>
        </div>
        <span
          data-testid={`driver-run-status-${run.id}`}
          className="text-[10px] uppercase tracking-[0.25em] text-[#0B0B0C] bg-[#E6E6E6] px-2.5 py-1"
        >
          {STATUS_LABELS[run.status]}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-2">
          <MapPin size={14} strokeWidth={1.25} className="text-[#8E9AA6] mt-0.5 shrink-0" />
          <span className="text-sm text-white font-light">{run.pickup_address}</span>
        </div>
        {!isHourly && run.dropoff_address && (
          <div className="flex items-start gap-2 pl-5">
            <span className="text-[#8E9AA6] text-xs">→</span>
            <span className="text-sm text-white font-light">{run.dropoff_address}</span>
          </div>
        )}
        {isHourly && (
          <div className="pl-5 text-[10px] uppercase tracking-[0.3em] text-amber-300/90">
            Hourly · {run.duration_hours || 3} hrs
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 text-[11px] text-[#8E9AA6] mb-3">
        <span className="flex items-center gap-1.5">
          <UsersIcon size={12} strokeWidth={1.5} /> {run.passengers}
        </span>
        <span className="flex items-center gap-1.5">
          <Briefcase size={12} strokeWidth={1.5} /> {run.luggage}
        </span>
        <span className="text-[#8E9AA6]">·</span>
        <span>{run.vehicle_category.replace(/_/g, " ")}</span>
      </div>

      {isAirport && run.flight && run.flight.valid && (
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] bg-white/5 border border-white/15 px-2.5 py-1.5 text-white mb-2">
          <Plane size={12} strokeWidth={1.5} />
          {run.flight_number} · {run.flight.status} · Gate {run.flight.gate}
        </div>
      )}

      {run.special_requests && (
        <div className="mt-3 border-t border-white/10 pt-3 text-[11px] text-[#8E9AA6] italic">
          “{run.special_requests}”
        </div>
      )}

      <div className="mt-4 flex justify-end text-[10px] uppercase tracking-[0.3em] text-white">
        Open run <ChevronRight size={12} strokeWidth={1.5} />
      </div>
    </button>
  );
}

function DriverProfilePanel({ user, profile, duty, onLogout }) {
  return (
    <div data-testid="driver-profile-panel" className="space-y-6 max-w-md">
      <div className="bg-[#121214]/60 border border-white/10 p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Chauffeur</div>
        <div className="font-serif text-2xl text-white mt-1">
          {profile?.first_name} {profile?.last_name}
        </div>
        <div className="text-sm text-[#8E9AA6] font-light mt-1">{user.email}</div>
        <div className="text-sm text-[#8E9AA6] font-light">{user.phone}</div>
      </div>

      <div className="bg-[#121214]/60 border border-white/10 p-6 space-y-3">
        <Row label="Duty" value={duty?.on_duty ? "On-Duty" : "Off-Duty"} />
        <Row label="Role" value={user.role} />
        <Row label="Member Since" value={new Date(user.created_at).toLocaleDateString()} />
      </div>

      <button
        onClick={onLogout}
        data-testid="driver-logout-btn"
        className="w-full inline-flex items-center justify-center bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-9 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
      >
        Sign out
      </button>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">{label}</span>
      <span className="text-sm text-white font-light">{value}</span>
    </div>
  );
}
